import { titleizeSlug, citationHostname, clampBody } from '../data/slug-title.js'
import { pathFor } from '../../../routes/index.js'

// Shared "wild facts that reference this entity" rail. Used by tick,
// disease, and technique pages to retro-fit the §B3 cross-link
// "every entity page links to every wild fact attached" promise.
//
// Hides itself entirely when there are no rows — the host page should
// pass `null` rather than render an empty section heading.

export interface FactsRailRow {
  id: number
  slug: string
  body: string
  citationUrl: string | null
}

export interface FactsRailProps {
  rows: ReadonlyArray<FactsRailRow>
  loading: boolean
  error?: Error | null
  /** Section heading. Defaults to "Wild facts". */
  heading?: string
  /** TestID prefix; defaults to "facts-rail". */
  testIdPrefix?: string
}

export function FactsRail({
  rows,
  loading,
  error,
  heading = 'Wild facts',
  testIdPrefix = 'facts-rail',
}: FactsRailProps) {
  if (error) {
    return (
      <section className="tp-section" data-testid={testIdPrefix}>
        <div className="head">
          <h2 className="tp-serif">{heading}</h2>
        </div>
        <p className="ui" style={{ color: 'var(--accent)', fontSize: 13 }}>
          Failed to load wild facts: {error.message}
        </p>
      </section>
    )
  }

  if (loading) {
    return (
      <section className="tp-section" data-testid={testIdPrefix}>
        <div className="head">
          <h2 className="tp-serif">{heading}</h2>
          <span className="meta">loading…</span>
        </div>
      </section>
    )
  }

  if (rows.length === 0) return null

  return (
    <section className="tp-section" data-testid={testIdPrefix}>
      <div className="head">
        <h2 className="tp-serif">{heading}</h2>
        <span className="meta">
          {rows.length} {rows.length === 1 ? 'fact' : 'facts'}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
        }}
      >
        {rows.map((row) => (
          <RailCard key={row.id} row={row} testIdPrefix={testIdPrefix} />
        ))}
      </div>
    </section>
  )
}

function RailCard({
  row,
  testIdPrefix,
}: {
  row: FactsRailRow
  testIdPrefix: string
}) {
  const title = titleizeSlug(row.slug)
  const monogram = (row.slug.trim()[0] ?? '?').toUpperCase()
  const preview = clampBody(row.body, 140)
  const citationLabel = row.citationUrl ? citationHostname(row.citationUrl) : null
  return (
    <a
      href={pathFor('fact', { slug: row.slug })}
      className="hairline"
      data-testid={`${testIdPrefix}-card`}
      style={{
        padding: 16,
        textDecoration: 'none',
        color: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: 'var(--surface)',
        borderRadius: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          className="hairline tp-serif"
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 999,
            fontSize: 14,
            color: 'var(--ink)',
          }}
        >
          {monogram}
        </span>
        <h3
          className="tp-serif"
          style={{
            fontSize: 16,
            lineHeight: 1.2,
            margin: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </h3>
      </div>
      <p
        className="tp-serif"
        style={{ fontSize: 14, color: 'var(--ink-2)', margin: 0 }}
      >
        {preview}
      </p>
      {citationLabel && (
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--muted)',
            paddingTop: 8,
            borderTop: '1px solid var(--hairline)',
          }}
        >
          source · {citationLabel}
        </div>
      )}
    </a>
  )
}
